import { X } from 'lucide-react'
import { Appointment } from '../../lib/api/appointments'

interface TimeSlot {
  time: string
  hour: number
  minute: number
  appointments: Appointment[]
}

interface TimeSlotModalProps {
  isOpen: boolean
  onClose: () => void
  slot: TimeSlot | null
  date: string
  allAppointments: Appointment[]
}

export function TimeSlotModal({ isOpen, onClose, slot, date, allAppointments }: TimeSlotModalProps) {
  if (!isOpen || !slot) return null

  // Get appointments in the hour before and after the clicked slot
  const getContextAppointments = () => {
    const slotHour = slot.hour
    const slotMinute = slot.minute
    
    // Calculate time range (hour before to hour after)
    const startHour = Math.max(0, slotHour - 1)
    const endHour = Math.min(23, slotHour + 1)
    
    const appointments = allAppointments.filter(apt => {
      if (apt.date !== date) return false
      
      const [aptStartHour, aptStartMin] = apt.start_time.split(':').map(Number)
      const [aptEndHour, aptEndMin] = apt.end_time.split(':').map(Number)
      
      const aptStartMinutes = aptStartHour * 60 + aptStartMin
      const aptEndMinutes = aptEndHour * 60 + aptEndMin
      const rangeStartMinutes = startHour * 60
      const rangeEndMinutes = (endHour + 1) * 60
      
      return (
        (aptStartMinutes >= rangeStartMinutes && aptStartMinutes < rangeEndMinutes) ||
        (aptEndMinutes > rangeStartMinutes && aptEndMinutes <= rangeEndMinutes) ||
        (aptStartMinutes <= rangeStartMinutes && aptEndMinutes >= rangeEndMinutes)
      )
    })
    
    // Sort by start time
    return appointments.sort((a, b) => a.start_time.localeCompare(b.start_time))
  }

  const contextAppointments = getContextAppointments()
  
  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':')
    const h = parseInt(hours)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const displayHour = h % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'badge-success'
      case 'confirmed': return 'badge-info'
      case 'cancelled': return 'badge-error'
      case 'scheduled': return 'badge-warning'
      default: return 'badge-ghost'
    }
  }

  return (
    <dialog className="modal modal-open">
      <div className="modal-box max-w-3xl">
        <button 
          className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
          onClick={onClose}
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="font-bold text-xl mb-2">
          Schedule for {formatTime(slot.time)}
        </h3>
        <p className="text-base-content/70 mb-4">
          {formatDate(date)}
        </p>

        <div className="divider">Context Window (Hour Before/After)</div>

        {contextAppointments.length === 0 ? (
          <div className="text-center py-8 text-base-content/50">
            <p>No appointments in this time window</p>
            <p className="text-sm mt-2">
              Showing {formatTime(`${Math.max(0, slot.hour - 1)}:00`)} - {formatTime(`${Math.min(23, slot.hour + 2)}:00`)}
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {contextAppointments.map(apt => {
              const isInClickedSlot = apt.start_time.substring(0, 5) === slot.time
              
              return (
                <div 
                  key={apt.id}
                  className={`card bg-base-200 ${isInClickedSlot ? 'border-2 border-accent shadow-lg' : ''}`}
                >
                  <div className="card-body p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold text-lg">{apt.customer.name}</span>
                          <span className={`badge ${getStatusColor(apt.status)}`}>
                            {apt.status}
                          </span>
                          {isInClickedSlot && (
                            <span className="badge badge-accent">Selected Time</span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-base-content/60">Time:</span>
                            <span className="ml-2 font-semibold">
                              {formatTime(apt.start_time)} - {formatTime(apt.end_time)}
                            </span>
                          </div>
                          <div>
                            <span className="text-base-content/60">Service:</span>
                            <span className="ml-2 font-medium">{apt.skill.name}</span>
                          </div>
                          <div>
                            <span className="text-base-content/60">Team Member:</span>
                            <span className="ml-2 font-medium">
                              {apt.team_member.user.full_name || apt.team_member.user.email.split('@')[0]}
                            </span>
                          </div>
                          {apt.customer.email && (
                            <div>
                              <span className="text-base-content/60">Customer Email:</span>
                              <span className="ml-2 text-xs">{apt.customer.email}</span>
                            </div>
                          )}
                        </div>

                        {apt.notes && (
                          <div className="mt-2 p-2 bg-base-300 rounded text-xs">
                            <span className="font-semibold">Notes:</span> {apt.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div className="modal-action">
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  )
}
